/**
 * CreateContactModal
 * Modal for creating new contacts from the Contacts dashboard
 * Supports Prospect, Organization Contact, and User types
 */

import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContact } from "../../services/contacts.service";
import { getOrganizationsForDropdown } from "../../services/partnerOffers.service";
import {
  getLocations,
  linkContactToLocations,
} from "../../services/locations.service";
import { useToast } from "../ui/Toast";
import { QUERY_KEYS } from "../../utils/constants";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import type { ContactType } from "../../types/list.types";
import type { Location } from "../../types/organization.types";
import type { UUID } from "../../types/common.types";

type CreatableContactType = "Prospect" | "Organization Contact";

const CREATABLE_TYPES: { value: CreatableContactType; label: string }[] = [
  { value: "Prospect", label: "Prospect" },
  { value: "Organization Contact", label: "Organization Contact" },
];

interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ContactFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  title: string;
  description: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip_code: string;
}

const emptyForm: ContactFormData = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  title: "",
  description: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  zip_code: "",
};

const CreateContactModal: React.FC<CreateContactModalProps> = ({
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [contactType, setContactType] =
    useState<CreatableContactType>("Prospect");
  const [formData, setFormData] = useState<ContactFormData>(emptyForm);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [locationMode, setLocationMode] = useState<"all" | "specific">("all");
  const [selectedLocationIds, setSelectedLocationIds] = useState<UUID[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Fetch organizations for dropdown
  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations-dropdown"],
    queryFn: getOrganizationsForDropdown,
    enabled: isOpen && contactType === "Organization Contact",
  });

  // Fetch locations when org is selected
  const { data: locationsData } = useQuery({
    queryKey: ["locations", selectedOrgId],
    queryFn: () => getLocations(selectedOrgId as UUID, { page: 1, limit: 100 }),
    enabled: !!selectedOrgId && contactType === "Organization Contact",
  });

  const locations: Location[] = locationsData?.data || [];
  const showLocationPicker = locations.length >= 2;

  // Reset org/location when type changes
  useEffect(() => {
    setSelectedOrgId("");
    setLocationMode("all");
    setSelectedLocationIds([]);
  }, [contactType]);

  const handleChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleLocationToggle = (locationId: UUID) => {
    setSelectedLocationIds((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId],
    );
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
        formData.email,
      )
    ) {
      newErrors.email = "Invalid email format";
    }
    if (contactType === "Organization Contact" && !selectedOrgId) {
      newErrors.organization = "Organization is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getLifecycleStage = (): string => {
    switch (contactType) {
      case "Prospect":
        return "lead";
      case "Organization Contact":
        return "partner";
    }
  };

  const saveContact = async (): Promise<boolean> => {
    if (!validate()) {
      return false;
    }

    setIsSaving(true);
    try {
      const contactData: Record<string, unknown> = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone_number: formData.phone_number.trim() || undefined,
        contact_types: [contactType as ContactType],
        lifecycle_stage: getLifecycleStage(),
        source: "manual",
      };

      // Type-specific fields
      if (contactType === "Organization Contact") {
        contactData.organization_id = selectedOrgId;
        if (formData.title.trim()) {
          contactData.title = formData.title.trim();
        }
        if (formData.description.trim()) {
          contactData.description = formData.description.trim();
        }
      } else if (contactType === "Prospect") {
        if (formData.description.trim()) {
          contactData.description = formData.description.trim();
        }
        if (formData.address_line1.trim()) {
          contactData.address_line1 = formData.address_line1.trim();
        }
        if (formData.address_line2.trim()) {
          contactData.address_line2 = formData.address_line2.trim();
        }
        if (formData.city.trim()) {
          contactData.city = formData.city.trim();
        }
        if (formData.state.trim()) {
          contactData.state = formData.state.trim();
        }
        if (formData.zip_code.trim()) {
          // The contacts column is `postal_code` (see Contact type / ContactForm).
          // This modal previously sent `zip_code`, which doesn't exist and threw
          // "Could not find the 'zip_code' column of 'contacts' in the schema cache"
          // when a tester entered a zip while creating a contact for a phone sale.
          contactData.postal_code = formData.zip_code.trim();
        }
      }

      const newContact = await createContact(contactData);

      // Link to specific locations if org contact with specific locations
      if (
        contactType === "Organization Contact" &&
        locationMode === "specific" &&
        selectedLocationIds.length > 0
      ) {
        await linkContactToLocations(newContact.id, selectedLocationIds);
      }

      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACTS] });
      showToast(
        "success",
        `Contact ${formData.first_name} ${formData.last_name} created`,
      );
      return true;
    } catch (error: any) {
      showToast("error", error.message || "Failed to create contact");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndClose = async () => {
    if (await saveContact()) {
      handleClose();
    }
  };

  const handleSaveAndAddAnother = async () => {
    if (await saveContact()) {
      setFormData(emptyForm);
      setSelectedOrgId("");
      setLocationMode("all");
      setSelectedLocationIds([]);
      setErrors({});
    }
  };

  const handleClose = () => {
    setFormData(emptyForm);
    setContactType("Prospect");
    setSelectedOrgId("");
    setLocationMode("all");
    setSelectedLocationIds([]);
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Contact"
      size="lg"
    >
      <div className="space-y-4">
        {/* Contact Type Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contact Type <span className="text-red-500">*</span>
          </label>
          <select
            value={contactType}
            onChange={(e) =>
              setContactType(e.target.value as CreatableContactType)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {CREATABLE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Common Fields */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={formData.first_name}
            onChange={(e) => handleChange("first_name", e.target.value)}
            error={errors.first_name}
            required
          />
          <Input
            label="Last Name"
            value={formData.last_name}
            onChange={(e) => handleChange("last_name", e.target.value)}
            error={errors.last_name}
            required
          />
        </div>

        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          error={errors.email}
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Phone"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => handleChange("phone_number", e.target.value)}
          />
          {contactType === "Organization Contact" && (
            <Input
              label="Title / Role"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="e.g. Store Manager"
            />
          )}
        </div>

        {/* Organization Dropdown (Org Contact only) */}
        {contactType === "Organization Contact" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedOrgId}
              onChange={(e) => {
                setSelectedOrgId(e.target.value);
                setLocationMode("all");
                setSelectedLocationIds([]);
                if (errors.organization) {
                  setErrors((prev) => ({ ...prev, organization: "" }));
                }
              }}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.organization ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select an organization...</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
            {errors.organization && (
              <p className="mt-1 text-sm text-red-600">{errors.organization}</p>
            )}
          </div>
        )}

        {/* Location Picker (Org Contact with 2+ locations) */}
        {contactType === "Organization Contact" &&
          selectedOrgId &&
          showLocationPicker && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Location Association
              </label>
              <div className="border border-gray-300 rounded-lg p-4 space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="locationMode"
                    checked={locationMode === "all"}
                    onChange={() => {
                      setLocationMode("all");
                      setSelectedLocationIds([]);
                    }}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Organization Level (All Locations)
                  </span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="locationMode"
                    checked={locationMode === "specific"}
                    onChange={() => setLocationMode("specific")}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Specific Locations
                  </span>
                </label>

                {locationMode === "specific" && (
                  <div className="pl-6 space-y-2 border-t border-gray-200 pt-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {locations.map((location) => (
                        <label
                          key={location.id}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLocationIds.includes(location.id)}
                            onChange={() => handleLocationToggle(location.id)}
                            className="h-4 w-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            {location.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Description/Notes (Prospect and Org Contact) */}
        {(contactType === "Prospect" ||
          contactType === "Organization Contact") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional notes about this contact..."
            />
          </div>
        )}

        {/* Address Fields (Prospect only) */}
        {contactType === "Prospect" && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">
              Address (optional)
            </p>
            <Input
              label="Address Line 1"
              value={formData.address_line1}
              onChange={(e) => handleChange("address_line1", e.target.value)}
            />
            <Input
              label="Address Line 2"
              value={formData.address_line2}
              onChange={(e) => handleChange("address_line2", e.target.value)}
            />
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="City"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />
              <Input
                label="State"
                value={formData.state}
                onChange={(e) => handleChange("state", e.target.value)}
              />
              <Input
                label="Zip Code"
                value={formData.zip_code}
                onChange={(e) => handleChange("zip_code", e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleSaveAndAddAnother}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save & Add Another"}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSaveAndClose}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save & Close"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateContactModal;
